// The decision at /auth/callback, extracted as a pure function so it's unit
// testable (the React effect just executes the verdict). This is the crux of
// the iOS-Safari "back to main page" fix: when the URL is an OAuth return but
// the session isn't ready yet, we WAIT rather than bounce.

export type CallbackAction =
  | "wait" // session not settled yet — stay on the callback screen
  | "route" // signed in — proceed to onboarding/dashboard/blend
  | "bounce"; // genuinely not signed in — send back to landing

export interface CallbackInput {
  /** Auth provider still hydrating the session. */
  loading: boolean;
  /** A user session exists. */
  hasUser: boolean;
  /** Current URL (used to detect an OAuth return: ?code= or #access_token=). */
  href: string;
  /** The grace period elapsed with still no session. */
  gaveUp: boolean;
}

/** True when the URL looks like a return from an OAuth provider. */
export function isOAuthReturn(href: string): boolean {
  return /[?&]code=|access_token=/.test(href);
}

export function decideCallbackAction({ loading, hasUser, href, gaveUp }: CallbackInput): CallbackAction {
  if (loading) return "wait";
  if (hasUser) return "route";
  // No user. If this is an OAuth return, the PKCE/implicit exchange may still be
  // in flight (iOS Safari is slow here) — wait it out. Only bounce once the
  // grace period expires, or when this clearly isn't an OAuth return at all.
  if (isOAuthReturn(href) && !gaveUp) return "wait";
  return "bounce";
}
